/**
 * Review wall: lightbox + "load more".
 *
 * The wall is already fully rendered by Liquid before this runs. Everything here
 * is optional, and every failure path ends in "the grid stays as it is".
 */
(function () {
  'use strict';
  var TIMEOUT_MS = 3000;
  var CHUNK = 8;

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function stars(n) {
    var pct = Math.max(0, Math.min(5, Number(n) || 0)) * 20;
    return '<span class="hz-stars hz-stars--sm"><span class="hz-stars__glyphs" style="--hz-pct:' +
      pct + '%" aria-hidden="true">★★★★★</span><span class="visually-hidden">' +
      esc(n) + ' / 5</span></span>';
  }

  var PLAY = '<span class="hz-card__play"><svg viewBox="0 0 64 64" aria-hidden="true">' +
    '<circle cx="32" cy="32" r="26" fill="rgba(255,255,255,.92)"/>' +
    '<path d="M26 21l19 11-19 11z" fill="#111"/></svg></span>';
  var CHECK = '<svg viewBox="0 0 16 16" aria-hidden="true"><path fill="currentColor" ' +
    'd="M6.2 11.6L2.9 8.3l1.2-1.2 2.1 2.1 5.6-5.6 1.2 1.2z"/></svg>';

  function cardHTML(r, tVerified) {
    var h = '<article class="hz-card"';
    if (r.m) h += ' data-hz-img="' + esc(r.m) + '"';
    if (r.vid) h += ' data-hz-video="' + esc(r.vid) + '"';
    if (r.vt) h += ' data-hz-vtype="' + esc(r.vt) + '"';
    h += ' data-hz-text="' + esc(r.b || '') + '">';
    if (r.m) {
      // Display copy for the tile; r.m stays the lightbox's full image.
      h += '<span class="hz-card__media"><img src="' + esc(r.mt || r.m) +
        '" alt="" loading="lazy" decoding="async">' + (r.vid ? PLAY : '') + '</span>';
    }
    h += '<div class="hz-card__body"><div class="hz-card__stars">' + stars(r.r) + '</div>' +
      '<div class="hz-card__name">' + esc(r.a || '');
    if (r.v) h += '<span class="hz-card__badge">' + CHECK + esc(tVerified) + '</span>';
    if (r.d) h += '<time class="hz-card__date" datetime="' + esc(r.d) + '">' + esc(r.d) + '</time>';
    h += '</div>';
    if (r.b) h += '<p class="hz-card__text">' + esc(r.b) + '</p>';
    return h + '</div></article>';
  }

  function openLightbox(card) {
    var img = card.getAttribute('data-hz-img');
    var vid = card.getAttribute('data-hz-video');
    var vtype = card.getAttribute('data-hz-vtype');
    if (!img && !vid) return;

    var lb = document.createElement('div');
    lb.className = 'hz-lb';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.innerHTML =
      '<button class="hz-lb__close" aria-label="Close">&times;</button><div class="hz-lb__inner">' +
      (vid
        // Self-hosted clips play natively; only videos still on a third-party
        // player need an iframe.
        ? (vtype === 'mp4'
            // Muted, because browsers block autoplay with sound outright — the
            // clip would just sit there paused. Controls let the viewer unmute.
            ? '<video src="' + esc(vid) + '" controls autoplay muted playsinline preload="metadata"' +
              (img ? ' poster="' + esc(img) + '"' : '') + '></video>'
            : '<iframe src="' + esc(vid) + '" allow="autoplay; fullscreen" allowfullscreen></iframe>')
        : '<img src="' + esc(img) + '" alt="">') +
      '<div class="hz-lb__cap">' + esc(card.getAttribute('data-hz-text') || '') + '</div></div>';

    function close() {
      lb.remove();
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    }
    function onKey(e) { if (e.key === 'Escape') close(); }

    lb.addEventListener('click', function (e) {
      if (e.target === lb || e.target.classList.contains('hz-lb__close')) close();
    });
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    document.body.appendChild(lb);
  }

  function init(root) {
    var grid = root.querySelector('[data-hz-grid]');
    var more = root.querySelector('[data-hz-more]');
    if (!grid) return;

    // Delegated, so cards appended later need no extra wiring.
    grid.addEventListener('click', function (e) {
      var card = e.target.closest ? e.target.closest('.hz-card') : null;
      if (card) openLightbox(card);
    });

    // A tile whose image 404s collapses to a text card rather than leaving a hole.
    grid.addEventListener('error', function (e) {
      if (e.target && e.target.tagName === 'IMG') {
        var m = e.target.closest('.hz-card__media');
        if (m) m.remove();
      }
    }, true);

    var proxy = root.getAttribute('data-hz-proxy');
    if (!proxy || !more) return;
    var tVerified = root.getAttribute('data-t-verified') || 'Verified';
    // What Liquid already put on the page. Sent with every request so the feed
    // starts exactly where the server-rendered cards stopped, whatever the
    // section's card count is set to.
    var served = grid.querySelectorAll('.hz-card').length;
    var page = 0, dead = false, busy = false, retried = false;

    function render(list) {
      var i = 0;
      (function step() {
        var slice = list.slice(i, i + CHUNK);
        if (!slice.length) return;
        var box = document.createElement('div');
        box.innerHTML = slice.map(function (r) { return cardHTML(r, tVerified); }).join('');
        var frag = document.createDocumentFragment();
        while (box.firstChild) frag.appendChild(box.firstChild);
        grid.appendChild(frag);
        i += CHUNK;
        if (i < list.length) requestAnimationFrame(step);
      })();
    }

    function load(p) {
      if (dead || busy) return;
      busy = true;
      more.setAttribute('aria-busy', 'true');
      var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = ctrl && setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS);

      fetch(proxy + '/v1/wall?after=' + served + '&page=' + p, {
        signal: ctrl ? ctrl.signal : undefined,
        headers: { accept: 'application/json' }
      })
        .then(function (res) {
          if (timer) clearTimeout(timer);
          if (!res.ok || res.status === 204) throw new Error('status ' + res.status);
          return res.json();
        })
        .then(function (data) {
          busy = false; more.removeAttribute('aria-busy');
          if (!data || !data.reviews || !data.reviews.length) { dead = true; more.hidden = true; return; }
          render(data.reviews);
          page = data.page;
          more.hidden = !data.has_more;
        })
        .catch(function () {
          if (timer) clearTimeout(timer);
          busy = false; more.removeAttribute('aria-busy');
          // One retry, then quiet for the rest of the pageview.
          if (!retried) { retried = true; setTimeout(function () { load(p); }, 600); }
          else { dead = true; more.hidden = true; }
        });
    }

    more.addEventListener('click', function () { load(page + 1); });

    if (window.Shopify && window.Shopify.designMode) return;

    // Reveal "load more" only once the backend has proven it can answer, so an
    // unreachable Worker leaves no button that does nothing when clicked.
    var probed = false;
    function probe() {
      if (probed) return;
      var r = root.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      if (r.top > vh + 300 || r.bottom < -300) return;
      probed = true;
      window.removeEventListener('scroll', probe);
      load(1);
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (es) {
        if (es.some(function (x) { return x.isIntersecting; })) { io.disconnect(); probe(); }
      }, { rootMargin: '300px' });
      io.observe(root);
    }
    window.addEventListener('scroll', probe, { passive: true });
    setTimeout(probe, 800);
  }

  function boot() {
    var roots = document.querySelectorAll('[data-hz-wall]');
    for (var i = 0; i < roots.length; i++) {
      try { init(roots[i]); } catch (e) { /* one wall must not break the others */ }
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
